import { useEffect, useSyncExternalStore } from 'react'
import { api, ApiError, isNetworkError } from '@shop/api-client'
import { deviceId, keys, read, subscribe, write } from './store'
import type { FailedOp, LocalShift, OpKind, PendingOp } from './store'

/**
 * العمليّاتُ المنتظرة — <b>تُحفظ بترتيبها وتُرسل بترتيبها</b> (`0052`).
 *
 * ⚠ **والترتيبُ شرط**: فتحُ الورديّة قبل بيعاتها، وبيعاتُها قبل إغلاقها. فتُرسل
 *   واحدةً واحدة، وأوّلُ انقطاعٍ يوقف الإرسالَ حتّى المحاولة التالية.
 *
 * ⚠ **وكلُّ عمليّةٍ باسم من سجّلها**: يُرسلها هو حين يدخل متّصلًا. فإن بدّل
 *   الكاشيرُ الجهازَ لغيره تنتظر عمليّاتُه حتّى يعود — ولا تُنسب لغيره.
 */

type NewOp = {
  kind: OpKind
  userId: string
  payload: Record<string, unknown>
  summary: string
  cash?: number
}

export function enqueue(op: NewOp): PendingOp {
  const pending: PendingOp = {
    clientId: crypto.randomUUID(),
    occurredAt: new Date().toISOString(),
    cash: 0,
    ...op,
  }

  write(keys.queue, [...read<PendingOp[]>(keys.queue, []), pending])

  return pending
}

export function pendingOps(): PendingOp[] {
  return read<PendingOp[]>(keys.queue, [])
}

export function failedOps(): FailedOp[] {
  return read<FailedOp[]>(keys.failed, [])
}

export function forgetFailed(clientId: string) {
  write(keys.failed, failedOps().filter((op) => op.clientId !== clientId))
}

const snapshotOf = () => localStorage.getItem('pos:offline:queue') + '|' + localStorage.getItem('pos:offline:failed')

/** العمليّاتُ المنتظرةُ والفاشلة — <b>وتُعاد الشاشةُ حين تتغيّر</b>. */
export function useQueue(): { pending: PendingOp[]; failed: FailedOp[] } {
  useSyncExternalStore(subscribe, snapshotOf)

  return { pending: pendingOps(), failed: failedOps() }
}

type Result = { clientId: string; status: 'DONE' | 'FAILED'; serverId: string | null; errorCode: string | null; error: string | null }

let running: Promise<void> | null = null

/**
 * يُرسل ما ينتظر لهذا الموظّف — <b>ومرّةً واحدةً في آنٍ واحد</b>.
 */
export function flush(userId: string): Promise<void> {
  running ??= send(userId).finally(() => {
    running = null
  })

  return running
}

async function send(userId: string) {
  for (;;) {
    const next = pendingOps().find((op) => op.userId === userId)

    if (!next) {
      return
    }

    let result: Result

    try {
      result = await api<Result>('/api/v1/offline/ops', {
        method: 'POST',
        body: {
          clientId: next.clientId,
          kind: next.kind,
          occurredAt: next.occurredAt,
          payload: next.payload,
          deviceId: deviceId(),
        },
      })
    } catch (failure) {
      // ⚠ **والانقطاعُ يوقف ولا يُسقط**: العمليّةُ تبقى في رأس القائمة.
      if (isNetworkError(failure) || (failure instanceof ApiError && failure.status === 401)) {
        return
      }

      result = {
        clientId: next.clientId,
        status: 'FAILED',
        serverId: null,
        errorCode: failure instanceof ApiError ? String(failure.status) : null,
        error: failure instanceof Error ? failure.message : 'خطأٌ غيرُ معروف',
      }
    }

    // ورديّةٌ فُتحت فاصلًا وصلت — فيُعرف رقمُها على الخادم من الآن.
    if (result.status === 'DONE' && next.kind === 'SHIFT_OPEN' && result.serverId) {
      const shift = read<LocalShift | null>(keys.shift, null)

      if (shift?.clientId === next.clientId) {
        write(keys.shift, { ...shift, serverId: result.serverId })
      }
    }

    if (result.status === 'FAILED') {
      write(keys.failed, [...failedOps(), { ...next, errorCode: result.errorCode, error: result.error }])
    }

    write(keys.queue, pendingOps().filter((op) => op.clientId !== next.clientId))
  }
}

/**
 * يُرسل تلقائيًّا: حين يعود الاتّصال، وكلَّ ٣٠ ثانيةً ما دام متّصلًا.
 */
export function useAutoSync(userId: string | null, online: boolean) {
  useEffect(() => {
    if (!userId || !online) {
      return
    }

    void flush(userId)

    const timer = window.setInterval(() => void flush(userId), 30_000)

    return () => window.clearInterval(timer)
  }, [userId, online])
}
